import { XMarkIcon } from "@heroicons/react/24/outline";
import { useRef } from "react";

import { FileUploadAndParse } from "@fronterahealth/frontera-ui-components";

import {
  useCreateTreatmentPlanCustomFieldFileMutation,
  useUpdateTreatmentPlanCustomFieldFileUrlMutation,
} from "@api/graphql/types-and-hooks";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";

interface TreatmentPlanImageProps {
  /** Global ID of the backing custom-field row, or null if it doesn't exist yet. */
  customFieldId: string | null;
  /** Signed URL of the currently-uploaded image, or null if none. */
  imageSignedUrl: string | null;
  disabled?: boolean;
  /**
   * Ensure a backing custom-field row exists and return its global id. Called
   * lazily on first upload so image fields don't create empty rows until used.
   * Returns null when the row can't be created (e.g. missing parent entity).
   */
  onEnsureCustomFieldId: () => Promise<string | null>;
  /** Called after a successful upload so the consumer can refetch the signed URL. */
  onUploaded: () => Promise<void> | void;
  /** Called to clear the current image (clears content + refetches). */
  onRemove?: () => Promise<void>;
}

// Report-writing runtime widget for goal/target custom fields of type IMAGE.
// Mirrors ReportFieldImage but persists through the treatment-plan custom-field
// file mutations, creating the backing custom-field row on demand when the
// clinician uploads their first image.
export const TreatmentPlanImage: React.FC<TreatmentPlanImageProps> = ({
  customFieldId,
  imageSignedUrl,
  disabled = false,
  onEnsureCustomFieldId,
  onUploaded,
  onRemove,
}) => {
  const createFileMutation = useCreateTreatmentPlanCustomFieldFileMutation({ throwOnError: false });
  const updateFileUrlMutation = useUpdateTreatmentPlanCustomFieldFileUrlMutation({ throwOnError: false });

  // Resolve the backing custom-field id ONCE per upload. The presign
  // (S3BucketUrlRetriever) and upload (S3BucketUploadCallback) are two separate
  // FileUploadAndParse callback props with no other channel to share the id, and
  // onEnsureCustomFieldId creates the row with a content:"" write (same as the
  // clear/remove path) — calling it in both callbacks can double-create the row.
  // The retriever resolves + stashes here; the upload callback reads it; it's
  // cleared when the upload settles/resets. (A ref is justified: the two props
  // share no other channel — the repo otherwise avoids refs for state.)
  const resolvedCustomFieldIdRef = useRef<string | null>(null);

  if (imageSignedUrl) {
    return (
      <div className="flex justify-center items-center rounded-lg border border-dashed border-limestone-200 px-6 transition relative hover:bg-limestone-50">
        <div className="rounded-xl">
          <img
            src={imageSignedUrl}
            alt="Invalid URL"
            className="max-w-full max-h-[32rem] object-contain p-2 rounded-xl"
          />
          {!disabled && onRemove && (
            <XMarkIcon
              onClick={() => {
                void onRemove();
              }}
              className="h-6 w-6 absolute top-2 right-2 cursor-pointer rounded-md border border-limestone-200 bg-white p-1 text-text-secondary transition-all hover:bg-red-200"
            />
          )}
        </div>
      </div>
    );
  }

  if (disabled) {
    return <ReportFieldDisabledValue />;
  }

  return (
    <FileUploadAndParse
      fileKind={customFieldId ?? "treatment-plan-custom-field-image"}
      existingFiles={[]}
      title=""
      singleFileUpload
      disabled={false}
      supportedFileTypes={["png", "jpg", "gif"]}
      maxSize={"100MB"}
      S3BucketUrlRetriever={async ({ fileName, mimeType, fileSize }) => {
        // First (and only) place onEnsureCustomFieldId is called per upload.
        const id = customFieldId ?? (await onEnsureCustomFieldId());
        if (!id) {
          throw new Error("Unable to resolve a custom field to attach the image to");
        }
        resolvedCustomFieldIdRef.current = id;
        const res = await createFileMutation.mutateAsync({
          input: { customFieldId: id, mimeType, originalFileName: fileName, fileSize },
        });
        return { bucket: res?.createTreatmentPlanCustomFieldFile?.fileUrl ?? "", fileId: id };
      }}
      S3BucketUploadCallback={async ({ file, url, mimeType }) => {
        // Reuse the id the retriever already resolved — never call
        // onEnsureCustomFieldId again (would repeat the content:"" create write).
        const id = customFieldId ?? resolvedCustomFieldIdRef.current;
        if (!id) {
          throw new Error("Unable to resolve a custom field to attach the image to");
        }
        try {
          const response = await fetch(url, { method: "PUT", headers: { "Content-Type": mimeType }, body: file });
          if (!response.ok) {
            throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
          }
          await updateFileUrlMutation.mutateAsync({
            input: { customFieldId: id, originalFileName: file.name },
          });
          await onUploaded();
        } finally {
          resolvedCustomFieldIdRef.current = null;
        }
      }}
      reportFileUploadCompleteCallback={async () => {
        await onUploaded();
      }}
      fileParseStatusRetriever={(fileId) => Promise.resolve({ fileId, status: "done" })}
      removeFileCallback={() => {
        resolvedCustomFieldIdRef.current = null;
        return Promise.resolve();
      }}
    />
  );
};
